import { noop, type Nullable } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { findSettingsChild } from '@adv-frontend/yastore-modules/settings-tree';
import { apiV1Request } from '#shared/api/requests';
import {
    type OpenApiGenerateAISeoForCollectionPageRequest,
    type OpenApiGenerateAISeoForCollectionPageResponseSuccess,
    OpenApiGenerateAISeoForCollectionPageUrl,
    type OpenApiGenerateAISeoForOtherPagesRequest,
    type OpenApiGenerateAISeoForOtherPagesResponseSuccess,
    OpenApiGenerateAISeoForOtherPagesUrl,
    type OpenApiGenerateAISeoForProductPageRequest,
    type OpenApiGenerateAISeoForProductPageResponseSuccess,
    OpenApiGenerateAISeoForProductPageUrl,
    type OpenApiSchemaSettingHierarchy,
} from '#shared/api/schema';
import { prepareBody } from '../lib/prepareBody';
import {
    type GetValueByPathRequest,
    type GetValueByPathResponses,
    type PatchValueByPathRequest,
    type PatchValues,
    type SettingsLeafPaths,
} from '../types';

const merchSettings = 'merchSettings' as const;

export const api = createApi({
    name: 'entities/merchSetting/api/api',
    baseQuery: createBaseQuery(apiV1Request),
    tagTypes: [merchSettings],
    endpoints: (builder) => {
        return {
            /** Получаем всю иерархию настроек  */
            getSettings: builder.query<OpenApiSchemaSettingHierarchy, unknown>({
                query: () => {
                    return {
                        url: '/settings/describe/',
                        method: 'GET',
                    };
                },
                providesTags: [merchSettings],
            }),
            /** Получаем отдельное значение листка ветки настроек по пути path  */
            getValueByPath: builder.query<
                GetValueByPathResponses[GetValueByPathRequest['path']],
                GetValueByPathRequest
            >({
                query: ({ path }) => {
                    return {
                        url: `settings/value${path}`,
                        method: 'GET',
                    };
                },
                transformResponse: (response: { value: string }) => JSON.parse(response.value),
            }),
            patchValues: builder.mutation<void, PatchValues>({
                query: (payload) => {
                    return {
                        url: `settings/values`,
                        method: 'PATCH',
                        body: payload.map(({ path, value }) => {
                            return { path, value: prepareBody(value) };
                        }),
                    };
                },
                async onQueryStarted(newSettings, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const updateSettings = (
                        settings: Nullable<OpenApiSchemaSettingHierarchy>,
                        { path, value }: PatchValues[number]
                    ) => {
                        if (settings) {
                            const leafPathArray = path.split('/').filter(Boolean);

                            const valueLeaf = findSettingsChild(settings, leafPathArray, path);

                            if (valueLeaf?.setting) {
                                valueLeaf.setting.value = prepareBody(value);
                            }
                        }
                    };

                    api.endpoints.getSettings.updateQueryData({}, (settings) => {
                        newSettings.forEach((setting) => updateSettings(settings, setting));
                    });
                },
                invalidatesTags: [merchSettings],
            }),

            /** Меняем отдельное значение листка ветки настроек по пути path  */
            patchValueByPath: builder.mutation<void, PatchValueByPathRequest<SettingsLeafPaths>>({
                query: ({ path, body }) => {
                    return {
                        url: `settings/value${path}`,
                        method: 'PATCH',
                        body: { value: prepareBody(body) },
                    };
                },
                async onQueryStarted({ body, path }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const updateSettings = (settings: Nullable<OpenApiSchemaSettingHierarchy>) => {
                        if (settings) {
                            const leafPathArray = path.split('/').filter(Boolean);

                            const valueLeaf = findSettingsChild(settings, leafPathArray, path);

                            if (valueLeaf?.setting) {
                                valueLeaf.setting.value = prepareBody(body);
                            }
                        }
                    };

                    api.endpoints.getSettings.updateQueryData({}, (settings) => {
                        updateSettings(settings);
                    });
                },
                invalidatesTags: [merchSettings],
            }),

            generateAISeoSettingsForProductPage: builder.mutation<
                OpenApiGenerateAISeoForProductPageResponseSuccess,
                OpenApiGenerateAISeoForProductPageRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiGenerateAISeoForProductPageUrl,
                        method: 'POST',
                        body: payload,
                    };
                },
            }),

            generateAISeoSettingsForListingPage: builder.mutation<
                OpenApiGenerateAISeoForCollectionPageResponseSuccess,
                OpenApiGenerateAISeoForCollectionPageRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiGenerateAISeoForCollectionPageUrl,
                        method: 'POST',
                        body: payload,
                    };
                },
            }),

            generateAISeoSettingsForOtherPages: builder.mutation<
                OpenApiGenerateAISeoForOtherPagesResponseSuccess,
                OpenApiGenerateAISeoForOtherPagesRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiGenerateAISeoForOtherPagesUrl,
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
        };
    },
});

export const useGetSettings = api.useGetSettingsQuery;
export const getSettingValueByPath = api.endpoints.getValueByPath.query;

export const generateAISeoSettingsForProductPage = api.endpoints.generateAISeoSettingsForProductPage.mutate;
export const generateAISeoSettingsForListingPage = api.endpoints.generateAISeoSettingsForListingPage.mutate;
export const generateAISeoSettingsForOtherPages = api.endpoints.generateAISeoSettingsForOtherPages.mutate;
